import { Box, Stack } from "@dropbox/dig-foundations";
import classNames from "classnames";
import * as React from "react";
import styles from "./SearchShowcase.module.css";

export interface SearchShowcaseProps {
  /**
   * The number of search results to display
   */
  numResults?: number;

  /**
   * Whether to animate the skeleton elements
   */
  animate?: boolean;
}

const SearchResultSkeleton = ({
  index,
  animate = true,
}: {
  index: number;
  animate?: boolean;
}) => {
  return (
    <Box
      display="flex"
      alignItems="center"
      columnGap="8"
      width="100%"
      data-testid="skeleton-result"
    >
      <Box
        className={classNames(styles.resultSkeletonIcon, {
          [styles.animated]: animate,
        })}
        style={{ animationDelay: `${index * 0.2}s` }}
      />
      <Stack gap="8" width="100%">
        <Box
          className={classNames(styles.resultSkeletonLine, {
            [styles.animated]: animate,
          })}
          width="100%"
          style={{ animationDelay: `${index * 0.2}s` }}
        />
        <Box
          className={classNames(styles.resultSkeletonLine, {
            [styles.animated]: animate,
          })}
          style={{ width: "75%", animationDelay: `${index * 0.2}s` }}
        />
      </Stack>
    </Box>
  );
};

/**
 * SearchShowcase displays a skeleton of the search results page.
 */
export const SearchShowcase: React.FC<SearchShowcaseProps> = ({
  numResults = 3,
  animate = true,
}) => {
  return (
    <Box
      width="100%"
      borderRadius="Large"
      borderColor="Border Subtle"
      borderWidth="1"
      paddingX="20"
      paddingY="24"
      boxShadow="Raised"
    >
      <Stack width="100%" height="100%" gap="20">
        <Box className={styles.searchBarSkeleton} />
        <Stack width="100%" gap="20">
          {Array.from({ length: numResults }).map((_, i) => (
            <SearchResultSkeleton
              key={`skeleton-result-${i}`}
              index={i}
              animate={animate}
            />
          ))}
        </Stack>
      </Stack>
    </Box>
  );
};
